import { INFERENCE_URL } from '@/lib/constants';
import { signalUpgradeRequiredIfNeeded, signalUsageLimit } from './paywall-signal';
import type { CalendarItemKind } from '@/lib/calendar/types';
import type { FocusView } from '@/lib/calendar/built-in-views';
import type {
  SmsDisconnectResponse,
  SmsOptoutResponse,
  SmsTestSendResponse,
  SmsTestStatusResponse,
  SmsVerifyBody,
  SmsVerifyResponse,
  SmsWebhookBody,
  SmsWebhookResponse,
} from '@/lib/sms-contracts';
import type {
  Account,
  AccountContact,
  Agent,
  AgentCustomization,
  AgentCustomizationResponse,
  AgentTrigger,
  AnalyticsData,
  ArtifactRow,
  BudgetPolicy,
  CalendarConnection,
  CalendarEvent,
  CeoSession,
  ChatResult,
  ChatWireResponse,
  Conversation,
  CrmActivity,
  CrmContact,
  CrmDeal,
  CrmPipeline,
  AccountBilling,
  AgentLearning,
  ApprovalRule,
  AuditEntry,
  Guardrail,
  GuardrailScope,
  GuardrailRuleType,
  GuardrailSeverity,
  SoulDoc,
  SoulDocHistoryEntry,
  Channel,
  ChannelsResponse,
  ChannelType,
  Decision,
  FeedbackEvent,
  FeedbackStatus,
  DashboardStats,
  SmsContact,
  MeteredPricing,
  Plan,
  SubscribeResponse,
  UsageSummary,
  HumanAction,
  Document,
  KnowledgeSource,
  Goal,
  GoalActivity,
  PlanGenerateInput,
  StrategicPlanDraft,
  LeadInboxInput,
  LeadInboxRow,
  LeadInboxStatus,
  CatalogAgent,
  MarketplaceSkill,
  GoalTreeNode,
  Message,
  Plugin,
  PluginAgentBinding,
  PluginConfigValues,
  MarketplacePlugin,
  Project,
  Room,
  RoomMessage,
  StarterKit,
  WorkflowData,
  InstancePayload,
  UpdateStatus,
  UpdateHistoryEntry,
  OrgMemberDto,
} from '@/lib/types';
import type {
  Workflow,
  WorkflowRun,
  WorkflowRunNode,
  DegradedRun,
  WorkflowTemplate,
  CreateWorkflowRequest,
  StartRunRequest,
  ResumeRunRequest,
} from '@/types/workflow';

/**
 * Loose shape for validation errors returned by `POST /api/workflows/:id/activate`
 * when the upstream backend detects an invalid graph (422). The server-side
 * shape is richer; we only need something the UI can display.
 */
export interface WorkflowValidationError {
  node_id?: string;
  edge?: { from: string; to: string };
  code?: string;
  message: string;
}

export interface ElevationStatus {
  elevated: boolean;
  expires_at: string | null;
  via: 'personal_pin' | 'admin_override_pin' | null;
  has_personal_pin: boolean;
  has_override_pin: boolean;
  is_locked: boolean;
  unlock_at: string | null;
  role: string;
}

export interface ElevationVerifyResponse {
  ok: boolean;
  expires_at?: string;
  via?: 'personal_pin' | 'admin_override_pin';
  noop?: boolean;
}

// ---------------------------------------------------------------------------
// Core fetch wrappers
// ---------------------------------------------------------------------------

/** Cap on the wait we honor from a Retry-After header, in milliseconds.
 * Beyond this, throwing immediately is friendlier than blocking the page
 * for 10+ seconds. */
const RETRY_AFTER_CAP_MS = 5_000;

/** Parse the Retry-After response header to milliseconds. Spec (RFC 7231)
 * allows either an integer delta-seconds or an HTTP-date. Returns null for
 * a missing/unparseable header so the caller can fall back to a default. */
function parseRetryAfter(header: string | null): number | null {
  if (!header) return null;
  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1_000;
  const date = Date.parse(header);
  if (!Number.isNaN(date)) return Math.max(0, date - Date.now());
  return null;
}

/** Fetch from local Next.js API routes (auth, data — hits Turso directly).
 *
 * Retries ONCE on 429, honoring the backend's Retry-After header (capped
 * at 5s). Transient bursts — e.g. a navigation that lands inside the
 * tail of a rate-limit window — recover transparently instead of
 * surfacing "Too many requests" to the user. Sustained 429s (the bucket
 * is genuinely full) still throw after the retry. */
/**
 * Humane paywall bridge. A 402 with code PLATFORM_SUBSCRIPTION_REQUIRED means the
 * account is authenticated but has no active plan. Rather than let every gated
 * call dead-end in an infinite skeleton or a raw error, fire ONE global event;
 * a shell-mounted listener (UpgradeRequiredModal) shows a single dismissible
 * modal with an explicit Upgrade CTA. Callers STILL receive the thrown typed
 * error, so their own catch/stop behavior is unchanged. Never fires on 401
 * (that is the existing login flow), and never on the pay pages themselves
 * (/upgrade, /settings/billing) to avoid a re-prompt loop.
 */
/** GET hub /api/account/free-tier (2026-09-26 free tier). */
export interface FreeTierStatus {
  enabled: boolean;
  /** The hub's entitlement verdict (paid || trialing). */
  entitled: boolean;
  state: 'paid' | 'trialing' | 'trial_expired' | 'plan_ended' | 'none';
  trial_started_at: string | null;
  trial_expires_at: string | null;
  days_remaining: number | null;
  usage: Record<'messages' | 'images' | 'videos', { used: number; limit: number; remaining: number }> | null;
  upgrade_url: string;
}

// Moved to ./paywall-signal (2026-09-26) so the WS client can fire the same
// event for free-tier `usageLimit` responses. Re-exported for existing importers.
export { UPGRADE_REQUIRED_EVENT } from './paywall-signal';

async function fetchLocal<T>(path: string, options?: RequestInit): Promise<T> {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('ctrl-a-token')
      : null;

  const init: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options?.headers as Record<string, string>),
    },
  };

  let res = await fetch(path, init);

  if (res.status === 429) {
    const retryAfterMs = Math.min(
      parseRetryAfter(res.headers.get('Retry-After')) ?? 1_000,
      RETRY_AFTER_CAP_MS,
    );
    await new Promise<void>((resolve) => setTimeout(resolve, retryAfterMs));
    res = await fetch(path, init);
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Request failed' }));
    // Surface the backend `code` + HTTP status on the thrown Error so callers can
    // branch on machine-readable codes (e.g. OVERRIDE_ELEVATION_REQUIRED,
    // OVERRIDE_PIN_NOT_SET) instead of substring-matching the human message.
    const e = new Error((error as { error?: string }).error || `API error: ${res.status}`) as Error & { code?: string; status?: number; detail?: unknown; retryAfterSec?: number };
    e.code = (error as { code?: string }).code;
    e.status = res.status;
    // Seconds from Retry-After on a non-429 refusal (e.g. the org chart's 503 busy).
    const retryAfterMs = parseRetryAfter(res.headers.get('Retry-After'));
    if (retryAfterMs !== null) e.retryAfterSec = Math.max(1, Math.ceil(retryAfterMs / 1000));
    // Structured context some routes send (e.g. the org chart's cycle chain).
    e.detail = (error as { detail?: unknown }).detail;
    signalUpgradeRequiredIfNeeded(res.status, e.code, error as { reason?: string; error?: string });
    throw e;
  }

  return res.json() as Promise<T>;
}

/** Fetch from inference backend (Express on :1045 — AI chat/LLM only) */
/** Is there a session JWT in this browser? Studio uses this to disable its
 *  generate tiles rather than fire a request that would silently bill to the
 *  `'api'` pseudo-account. SSR-safe: always false on the server. */
/**
 * Did the server reject our CREDENTIAL, as opposed to the request failing?
 *
 * 401 is the answer to "who are you" going wrong. A network error, a 500, or an
 * offline device are not statements about the token and must never discard a
 * working session — a user on a flaky connection should not be logged out.
 *
 * 403 is deliberately NOT included: it means authenticated-but-not-allowed
 * (unpaid plan, login allowlist, insufficient role). Clearing the token there
 * would log people out of the paywall instead of showing it to them.
 */
export function isAuthRejection(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  const code = (err as { code?: string } | null)?.code;
  return status === 401 || code === 'NO_TOKEN' || code === 'INVALID_TOKEN';
}

/**
 * Drop a session the server has rejected.
 *
 * Clears the same keys as `auth.logout()` — deliberately, because a stale token
 * leaves behind the same wreckage as a user switch. `ctrl-a-active-conv` /
 * `ctrl-a-active-agent` point at the OLD account's conversation; leaving them
 * makes chat-context resume into `WS select-agent rejected: conversation
 * ownership mismatch` on the next sign-in.
 *
 * Unlike logout() this does NOT redirect. The session hook and the workspace
 * layout already route an unauthenticated user to /login, and a hard
 * `window.location` here would interrupt whatever the user was typing. This
 * function makes the state honest; routing stays where it already lives.
 */
export function clearStaleSession(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('ctrl-a-token');
  localStorage.removeItem('ctrl-a-api-key');
  localStorage.removeItem('ctrl-a-active-conv');
  localStorage.removeItem('ctrl-a-active-agent');
}

export function hasAuthToken(): boolean {
  return typeof window !== 'undefined' && !!localStorage.getItem('ctrl-a-token');
}

async function fetchInference<T>(path: string, options?: RequestInit): Promise<T> {
  const appKey = process.env.NEXT_PUBLIC_APP_KEY || '';
  // Send the session JWT alongside the app key. Without it the backend falls
  // back to `callerAccountId = 'api'` (routes.ts), which (a) 404s every
  // account-owned custom agent at the tenant gate (routes.ts:878), (b) bills
  // the turn to nobody, and (c) mints an orphan conversation per call.
  // Mirrors fetchLocal. Spec:
  // docs/superpowers/specs/2026-08-22-studio-rest-auth-attribution-design.md
  const token =
    typeof window !== 'undefined' ? localStorage.getItem('ctrl-a-token') : null;

  const res = await fetch(`${INFERENCE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-app-key': appKey,
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options?.headers as Record<string, string>),
    },
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Request failed' }));
    // Surface the backend `code` + HTTP status on the thrown Error so callers can
    // branch on machine-readable codes (e.g. OVERRIDE_ELEVATION_REQUIRED,
    // OVERRIDE_PIN_NOT_SET) instead of substring-matching the human message.
    const e = new Error((error as { error?: string }).error || `API error: ${res.status}`) as Error & { code?: string; status?: number };
    e.code = (error as { code?: string }).code;
    e.status = res.status;
    signalUpgradeRequiredIfNeeded(res.status, e.code, error as { reason?: string; error?: string });
    throw e;
  }

  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Chat attachment upload — multipart to /api/chat-upload proxy
// ---------------------------------------------------------------------------

export interface UploadedAttachment {
  id: string;
  filename: string;
  size: number;
  url: string;
}

export async function uploadChatAttachment(
  file: File,
  signal?: AbortSignal,
): Promise<UploadedAttachment> {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('ctrl-a-token')
      : null;

  const form = new FormData();
  form.append('file', file);

  const res = await fetch('/api/chat-upload', {
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
    body: form,
    signal,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: `Upload failed (${res.status})` }));
    throw new Error((body as { error?: string }).error || `Upload failed (${res.status})`);
  }

  return res.json() as Promise<UploadedAttachment>;
}

// ---------------------------------------------------------------------------
// Domain-organised API client
// ---------------------------------------------------------------------------

/** Account tier response — Wave E (Tier Orchestration Model, locked 2026-04-16). */
export interface TierResponse {
  tier: 1 | 2 | 3 | 4;
  name: string;
  full_name: string;
  discount_pct: number;
  can_orchestrate: boolean;
  upgradeUrl: string;
}

/** A minted access code (Phase 2b). Mirrors hub DBAccessCode. */
export interface AccessCode {
  code: string;
  duration_days: number | null;
  tier: string;
  max_redemptions: number | null;
  redemptions_used: number;
  code_expires_at: string | null;
  note: string | null;
  created_by: string | null;
  created_at: string;
  revoked_at: string | null;
}

/** The output guardrail refused this answer.
 *
 *  Arrives as HTTP 200 with a non-empty human refusal sentence in `response`
 *  plus `blocked: true` — so it is invisible to any emptiness test. Callers
 *  must render this as a REFUSAL, not as a finished generation. */
export class GuardrailBlockedError extends Error {
  readonly code = 'GUARDRAIL_BLOCKED';
  readonly violations: string[];
  constructor(message: string, violations: string[] = []) {
    super(message || 'That request was blocked by a safety policy.');
    this.name = 'GuardrailBlockedError';
    this.violations = violations;
  }
}

/** POST /api/chat answered 200 but carried no answer text.
 *
 *  A distinct type (not a bare Error) so the UI can tell "the model produced
 *  nothing" apart from "the network or auth failed" — Studio renders those
 *  very differently, and collapsing them is how a guardrail block ends up
 *  looking like a crash. */
export class EmptyChatResponseError extends Error {
  readonly code = 'EMPTY_CHAT_RESPONSE';
  constructor(message = 'The agent returned an empty response.') {
    super(message);
    this.name = 'EmptyChatResponseError';
  }
}

/** POST /api/chat answered 200, but the trial ran out mid-turn. Separate from
 *  EmptyChatResponseError because the user action differs: upgrade, not retry. */
export class TrialExpiredError extends Error {
  readonly code = 'TRIAL_EXPIRED';
  constructor(message = 'Your trial has ended. Upgrade to keep generating.') {
    super(message);
    this.name = 'TrialExpiredError';
  }
}

/** Translate the POST /api/chat wire body into the UI-side `ChatResult`.
 *
 *  The ONLY place that knows the backend calls the answer `response` while the
 *  UI calls it `content`. Everything downstream may assume `content` is a real
 *  string — that guarantee is this function's whole job, and the reason the
 *  original `result.content.slice(...)` crash cannot recur.
 *
 *  Empty-answer policy (decided 2026-08-22): THROW rather than return `''`.
 *  A 200 with no text is not a success — it is a guardrail block, an expired
 *  trial, or a backend fault. Returning an empty string renders a blank "done"
 *  card and tells the user their generation worked when it did not, which is
 *  the exact silent-failure class the Studio audit was written about.
 *
 *  @param wire  Untrusted JSON body as received from the backend.
 *  @returns     A `ChatResult` whose `content` is guaranteed to be a non-empty string.
 *  @throws      TrialExpiredError | EmptyChatResponseError
 */
export function normalizeChatResult(wire: ChatWireResponse): ChatResult {
  const {
    response,
    conversationId: _conversationId,
    agentId: _agentId,
    agentName: _agentName,
    timestamp: _timestamp,
    trialExpired,
    blocked,
    violations,
    ...rest
  } = wire;

  const content = typeof response === 'string' ? response.trim() : '';

  // A guardrail refusal is a NON-EMPTY 200. It must be checked before the
  // emptiness test, or it sails through as a successful generation and gets
  // rendered (and, once outputs are persisted, SAVED) as real output.
  if (blocked) throw new GuardrailBlockedError(content, violations ?? []);

  if (!content) {
    // Order matters: an expired trial is the more specific, more actionable
    // cause, and it also produces an empty response. Check it first.
    if (trialExpired) throw new TrialExpiredError();
    // A turn that ran tools and emitted no prose is legitimate — main chat's
    // offline fallback (chat-context.tsx) renders it as a quiet turn rather
    // than an error. Only a turn with NOTHING to show is a failure.
    if (rest.toolCalls?.length) return { ...rest, content: '' };
    throw new EmptyChatResponseError();
  }

  return { ...rest, content };
}

export const api = {
  // ── Auth (local → Turso) ─────────────────────────────────────────────────
  auth: {
    async login(email: string, password: string): Promise<{ token: string; account: Account }> {
      const data = await fetchLocal<{ token: string; account: Account }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      if (typeof window !== 'undefined') {
        localStorage.setItem('ctrl-a-token', data.token);
        localStorage.setItem('ctrl-a-api-key', data.token);
      }
      return data;
    },

    async signup(
      email: string,
      password: string,
      name: string,
      opts?: {
        promo_code?: string;
        business_name?: string;
        beta_consent?: boolean;
        checkout?: boolean;
        onboarding_data?: { business_name?: string; vertical?: string; primary_goal?: string; [key: string]: unknown };
      },
    ): Promise<{
      // Phase 1 (signup+paywall): signup no longer mints a usable session. The
      // account is created UNVERIFIED and the response carries verificationRequired
      // instead of a token — the caller shows a "check your email" state. `token`
      // is kept optional for backward-compat / the Quickstart prepay redirect.
      verificationRequired?: boolean;
      email?: string;
      token?: string;
      account: Account;
      checkoutUrl?: string | null;
      onboarding_conversation_id?: string | null;
    }> {
      const data = await fetchLocal<{
        verificationRequired?: boolean;
        email?: string;
        token?: string;
        account: Account;
        checkoutUrl?: string | null;
        onboarding_conversation_id?: string | null;
      }>('/api/auth/signup', {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          name,
          promo_code: opts?.promo_code,
          business_name: opts?.business_name,
          beta_consent: opts?.beta_consent,
          checkout: opts?.checkout,
          onboarding_data: opts?.onboarding_data,
        }),
      });
      // Only persist a session if the server actually returned one (it does not
      // in the verify-then-use flow). Guard prevents storing `undefined`.
      if (typeof window !== 'undefined' && data.token) {
        localStorage.setItem('ctrl-a-token', data.token);
        localStorage.setItem('ctrl-a-api-key', data.token);
      }
      return data;
    },

    // Phase 1: consume an email-verification token (proxies to the hub). Called
    // by the /verify-email page on load.
    async verifyEmail(token: string): Promise<{ ok?: boolean; email?: string; error?: string; code?: string }> {
      return fetchLocal('/api/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
    },

    // Phase 1: request a fresh verification email. Non-committal by design —
    // resolves { ok: true } regardless of whether the account exists/needs it.
    async resendVerification(email: string): Promise<{ ok: boolean }> {
      return fetchLocal('/api/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    },

    async session(): Promise<Account | null> {
      const token = typeof window !== 'undefined' ? localStorage.getItem('ctrl-a-token') : null;
      if (!token) return null;
      try {
        const data = await fetchLocal<{ account: Account }>('/api/auth/session', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        return data.account;
      } catch (err) {
        // A DEAD TOKEN AND A FLAKY NETWORK ARE DIFFERENT THINGS (2026-09-18).
        //
        // This was `catch { return null }`. Both cases returned null, and the dead
        // token stayed in localStorage — so the browser kept presenting it forever:
        // every API call 401'd, the WS logged `JWT present but verifyToken
        // rejected`, and chat-context went on trying to resume a conversation owned
        // by the expired token's account, producing
        // `WS select-agent rejected: conversation ownership mismatch` on repeat.
        // The UI still looked signed in the whole time.
        //
        // fetchLocal already attaches `status`, so this is a fact we can read rather
        // than a guess. On rejection we clear exactly what logout() clears; on a
        // network failure we keep the session and let the caller retry.
        if (isAuthRejection(err)) clearStaleSession();
        return null;
      }
    },

    logout(): void {
      if (typeof window !== 'undefined') {
        // Clear auth credentials.
        localStorage.removeItem('ctrl-a-token');
        localStorage.removeItem('ctrl-a-api-key');
        // Clear per-account session state. Without this, logging in as a
        // different user keeps the previous user's conversationId / agentId
        // in storage; chat-context tries to resume them on mount, the
        // backend rejects with `WS select-agent rejected: conversation
        // ownership mismatch`, and the user gets stuck. Theme is a global
        // preference, not per-account, so it stays.
        localStorage.removeItem('ctrl-a-active-conv');
        localStorage.removeItem('ctrl-a-active-agent');
        window.location.href = '/login';
      }
    },
  },

  // ── Models (local proxy → gateway GET /v1/models, static fallback) ──────
  models: {
    list(): Promise<{
      models: Array<{
        id: string;
        label: string;
        provider: string;
        /** @deprecated mirrors is_default. */
        default?: boolean;
        is_default?: boolean;
        included?: boolean;
        input_usd_micros_per_mtok?: number | null;
        output_usd_micros_per_mtok?: number | null;
        image_usd_micros_per_image?: number | null;
      }>;
    }> {
      return fetchLocal('/api/models');
    },
  },

  // ── Prepaid Credit Wallet (local proxy → gateway) ───────────────────────
  // Spec: docs/superpowers/specs/2026-07-01-v2-prepaid-credits-ui-design.md
  creditWallet: {
    get(): Promise<{
      balance_cents: number;
      total_purchased_cents: number;
      total_used_cents: number;
      total_expired_cents: number;
      next_expiry_at: string | null;
      last_topup_at: string | null;
      enabled: boolean;
      presets_cents: number[];
    }> {
      return fetchLocal('/api/account/credit-wallet');
    },

    checkout(amountCents: number): Promise<{ url: string; sessionId: string; amount_cents: number }> {
      return fetchLocal('/api/account/billing/credit-checkout', {
        method: 'POST',
        body: JSON.stringify({ amount_cents: amountCents }),
      });
    },
  },

  // ── Tools (proxy → workspace-main) ──────────────────────────────────────
  // Per-agent tool list — combines DB allowlist + runtime tier gates +
  // sandbox profile, so it cannot be derived from a Turso query alone.
  // Spec: docs/superpowers/specs/2026-05-28-tools-endpoint-ui-fix-design.md
  tools: {
    /**
     * The handler is `agent.getAvailableTools()`, which returns full
     * `ToolDefinition`s — including the JSON-Schema `parameters` block. The
     * type previously stopped at name+description, which understated what the
     * endpoint actually returns; the docs tool reference renders the schema.
     */
    byAgent(agentId: string): Promise<{
      agentId: string;
      tools: Array<{
        name: string;
        description?: string;
        parameters?: {
          type?: string;
          properties?: Record<string, { type?: string; description?: string; enum?: string[] }>;
          required?: string[];
        };
      }>;
      total: number;
    }> {
      return fetchLocal(`/api/tools/${encodeURIComponent(agentId)}`);
    },
  },

  // ── Agents (local → Turso) ───────────────────────────────────────────────
  agents: {
    list(): Promise<Agent[]> {
      return fetchLocal('/api/agents');
    },

    get(id: string): Promise<Agent> {
      return fetchLocal(`/api/agents/${id}`);
    },

    update(id: string, data: Partial<Agent>): Promise<Agent> {
      return fetchLocal(`/api/agents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    create(data: {
      name: string;
      color?: string | null;
      avatar_emoji?: string | null;
      avatar_image?: string | null;
      trust_level?: string | null;
      tools?: string[];
      role?: string | null;
      vertical?: string | null;
      personality?: string | null;
    }): Promise<{ id: string; name: string; role: string; vertical: string; color: string | null }> {
      return fetchLocal('/api/agents', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    // ── Knowledge grounding (v56 unified) ──
    getKnowledge(agentId: string): Promise<{
      agent_id: string;
      expert_name: string | null;
      knowledge_status: 'none' | 'draft' | 'processing' | 'ready' | 'error';
      knowledge_error_message: string | null;
      knowledge_summary: string;
      sources: Array<{ id: string; filename: string; source_type: string; char_count: number; status: string; created_at: string }>;
    }> {
      return fetchLocal(`/api/agents/${agentId}/knowledge`);
    },

    async uploadKnowledgeFile(agentId: string, file: File): Promise<{ id: string; filename: string; charCount: number; status: string }> {
      const form = new FormData();
      form.append('file', file);
      const token = typeof window !== 'undefined' ? localStorage.getItem('ctrl-a-token') : null;
      const res = await fetch(`/api/agents/${agentId}/knowledge/sources`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error((err as { error?: string }).error || `Upload failed: ${res.status}`);
      }
      return res.json();
    },

    uploadKnowledgeText(agentId: string, text: string, filename?: string): Promise<{ id: string; filename: string; charCount: number; status: string }> {
      return fetchLocal(`/api/agents/${agentId}/knowledge/sources`, {
        method: 'POST',
        body: JSON.stringify({ text, filename }),
      });
    },

    deleteKnowledgeSource(agentId: string, sourceId: string): Promise<{ ok: boolean }> {
      return fetchLocal(`/api/agents/${agentId}/knowledge/sources/${sourceId}`, {
        method: 'DELETE',
      });
    },

    buildKnowledge(agentId: string): Promise<{ status: string; message: string }> {
      return fetchLocal(`/api/agents/${agentId}/knowledge/build`, {
        method: 'POST',
      });
    },

    // ── Customization (edit an existing agent's prompt layers) ──
    getCustomization(agentId: string): Promise<AgentCustomizationResponse> {
      return fetchLocal(`/api/agents/${agentId}/customization`);
    },

    updateCustomization(agentId: string, data: AgentCustomization): Promise<{ ok: boolean; agentId: string }> {
      return fetchLocal(`/api/agents/${agentId}/customization`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    /**
     * Update an agent's trust level. Requires PIN elevation cookie on the
     * backend (admin_elevation). On 401 with code ELEVATION_REQUIRED /
     * ELEVATION_INVALID, callers should open the PinModal via
     * useElevation().require() and retry.
     *
     * Spec: ctrl-a_workspace-main/docs/superpowers/specs/2026-05-07-agent-trust-level-ui-design.md
     */
    updateTrust(
      agentId: string,
      trust_level: 'sandbox' | 'restricted' | 'standard' | 'autonomous',
    ): Promise<{
      ok: boolean;
      agentId: string;
      trust_level: string;
      previous_trust_level: string;
      changed: boolean;
    }> {
      return fetchLocal(`/api/agents/${agentId}/trust`, {
        method: 'PATCH',
        body: JSON.stringify({ trust_level }),
      });
    },

    clearCustomization(agentId: string): Promise<{ ok: boolean; agentId: string }> {
      return fetchLocal(`/api/agents/${agentId}/customization`, {
        method: 'DELETE',
      });
    },

    previewPrompt(agentId: string, overrides?: AgentCustomization): Promise<{ prompt: string }> {
      return fetchLocal(`/api/agents/${agentId}/preview-prompt`, {
        method: 'POST',
        body: JSON.stringify(overrides ?? {}),
      });
    },

    // ── Learnings (per-agent recall pipeline) ──
    // Powers the Agent Learning Log panel. Spec: punch list 3.2 (2026-05-09).
    listLearnings(
      agentId: string,
      filters?: { type?: AgentLearning['type']; minConfidence?: number },
    ): Promise<{ learnings: AgentLearning[] }> {
      const qs = new URLSearchParams();
      if (filters?.type) qs.set('type', filters.type);
      if (filters?.minConfidence !== undefined) qs.set('min_confidence', String(filters.minConfidence));
      const tail = qs.toString();
      return fetchLocal(`/api/agents/${agentId}/learnings${tail ? `?${tail}` : ''}`);
    },
    // Edit a learning's insight/confidence. Backend PUT accepts insight and/or
    // confidence only (type is immutable). Ownership enforced server-side.
    updateLearning(
      id: string,
      patch: { insight?: string; confidence?: number },
    ): Promise<{ ok: true }> {
      return fetchLocal(`/api/learnings/${id}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
    },
    // Forget (hard-delete) a learning. Ownership enforced server-side.
    deleteLearning(id: string): Promise<{ ok: true }> {
      return fetchLocal(`/api/learnings/${id}`, { method: 'DELETE' });
    },
  },

  // ── Chat (inference backend — AI/LLM) ────────────────────────────────────
  chat: {
    // The backend emits the answer as `response` (routes.ts, POST /chat tail);
    // every UI consumer expects `content`. That mismatch shipped undetected
    // because `fetchInference` casts the body with `as Promise<T>` — an
    // unchecked assertion, so the compiler "proved" a field the wire never
    // sends. Normalise here, at the one seam, rather than teaching callers
    // about the wire shape. Contract pinned by tests/unit/chat-rest-contract-guard.test.ts.
    async send(agentId: string, message: string, conversationId?: string): Promise<ChatResult> {
      const wire = await fetchInference<ChatWireResponse>('/api/chat', {
        method: 'POST',
        body: JSON.stringify({ agentId, message, conversationId }),
      });
      // Free tier: a refused turn still returns its humane text; also open the paywall.
      signalUsageLimit(wire.usageLimit, wire.response);
      return normalizeChatResult(wire);
    },

    conversations(agentId?: string): Promise<Conversation[]> {
      const qs = agentId ? `?agent_id=${agentId}` : '';
      return fetchLocal(`/api/conversations${qs}`);
    },

    messages(conversationId: string): Promise<Message[]> {
      return fetchLocal(`/api/conversations/${conversationId}/messages`);
    },

    // Soft-archive a 1:1 conversation. Spec:
    // ctrl-a_workspace-main/docs/superpowers/specs/2026-05-04-room-chat-archive-and-projects-contacts-design.md.
    deleteConversation(conversationId: string): Promise<{ ok: true; id: string; archived_at: string }> {
      return fetchLocal(`/api/conversations/${conversationId}`, { method: 'DELETE' });
    },
  },

  // ── Goals (local → Turso) ────────────────────────────────────────────────
  goals: {
    tree(): Promise<GoalTreeNode[]> {
      return fetchLocal('/api/goals/tree');
    },

    list(filters?: Record<string, string>): Promise<Goal[]> {
      const qs = filters ? '?' + new URLSearchParams(filters).toString() : '';
      return fetchLocal(`/api/goals${qs}`);
    },

    create(data: { title: string; level?: string; parentId?: string }): Promise<Goal> {
      return fetchLocal('/api/goals', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    update(id: string, data: Partial<Goal>): Promise<Goal> {
      return fetchLocal(`/api/goals/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    delete(id: string): Promise<{ ok: true }> {
      return fetchLocal(`/api/goals/${id}`, { method: 'DELETE' });
    },

    // Goal activity trail (spec 2026-05-04-goal-activity-trail-design.md).
    trail(
      id: string,
      opts?: { limit?: number; offset?: number },
    ): Promise<{ entries: GoalActivity[]; total: number }> {
      const qs = opts
        ? '?' +
          new URLSearchParams(
            Object.fromEntries(
              Object.entries(opts).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)]),
            ),
          ).toString()
        : '';
      return fetchLocal(`/api/goals/${id}/trail${qs}`);
    },

    addTrailNote(id: string, note: string): Promise<{ id: string }> {
      return fetchLocal(`/api/goals/${id}/trail`, {
        method: 'POST',
        body: JSON.stringify({ note }),
      });
    },

    // ── Strategic Plan Generation (Phase 1a) ────────────────────────────────
    // Proxy routes live at /api/goals/plan/* and forward to workspace-main
    // /api/planning/*. See spec: docs/superpowers/specs/2026-05-21-strategic-plan-generation-design.md
    plan: {
      async generate(input: PlanGenerateInput): Promise<StrategicPlanDraft> {
        // workspace-main wraps the result in a { draft } envelope — unwrap it.
        const { draft } = await fetchLocal<{ draft: StrategicPlanDraft }>('/api/goals/plan', {
          method: 'POST',
          body: JSON.stringify(input),
        });
        return draft;
      },

      async get(id: string): Promise<StrategicPlanDraft> {
        const { draft } = await fetchLocal<{ draft: StrategicPlanDraft }>(
          `/api/goals/plan/${id}`,
        );
        return draft;
      },

      approve(id: string): Promise<{ root_goal_id: string }> {
        return fetchLocal(`/api/goals/plan/${id}/approve`, { method: 'POST' });
      },

      discard(id: string): Promise<{ ok: true }> {
        return fetchLocal(`/api/goals/plan/${id}/discard`, { method: 'POST' });
      },
    },
  },

  // ── Projects (local → Turso) ─────────────────────────────────────────────
  projects: {
    list(): Promise<Project[]> {
      return fetchLocal('/api/projects');
    },

    get(id: string): Promise<Project> {
      return fetchLocal(`/api/projects/${id}`);
    },

    create(data: { name: string; description?: string; priority?: string }): Promise<Project> {
      return fetchLocal('/api/projects', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    update(id: string, data: Partial<Project>): Promise<Project> {
      return fetchLocal(`/api/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    delete(id: string): Promise<Project> {
      return fetchLocal(`/api/projects/${id}`, { method: 'DELETE' });
    },
  },

  // ── Budgets (local → Turso) ──────────────────────────────────────────────
  budgets: {
    list(): Promise<BudgetPolicy[]> {
      return fetchLocal('/api/budgets');
    },

    status(): Promise<{ policies: unknown[]; incidents: unknown[] }> {
      return fetchLocal('/api/budgets/status');
    },

    costs(params?: { agent_id?: string; window?: string }): Promise<unknown> {
      const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
      return fetchLocal(`/api/budgets/costs${qs}`);
    },
  },

  // ── Decisions (local → Turso) ────────────────────────────────────────────
  decisions: {
    pending(): Promise<Decision[]> {
      return fetchLocal('/api/decisions/pending');
    },

    async count(): Promise<number> {
      const data = await fetchLocal<{ count: number }>('/api/decisions/count');
      return data.count;
    },

    resolve(
      id: string,
      status: 'approved' | 'denied',
      note?: string,
      scope?: 'once' | 'session' | 'always',
    ): Promise<Decision> {
      return fetchLocal(`/api/decisions/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ status, note, scope }),
      });
    },

    listAll(filters?: Record<string, string>): Promise<Decision[]> {
      const qs = filters ? '?' + new URLSearchParams(filters).toString() : '';
      return fetchLocal(`/api/decisions${qs}`);
    },
  },

  // ── Integrations (Drive / Dropbox / Calendar OAuth) ──────────────────────
  integrations: {
    list(): Promise<{
      available: Array<{ provider: string; name: string; supported_tiers: number[] }>;
      connected: Array<{
        id: string;
        provider: 'google_drive' | 'dropbox' | 'google_calendar';
        account_label: string;
        status: 'connected' | 'disconnected' | 'needs_reauth' | 'error';
        scopes: string[];
        external_account_id: string;
        connected_at: string;
        last_sync_at: string | null;
        last_sync_error: string | null;
      }>;
    }> {
      return fetchLocal('/api/integrations');
    },

    /**
     * Connected apps (Composio). A DIFFERENT system from the native
     * Drive/Dropbox/Calendar calls around it: Composio owns the OAuth, the
     * tokens and the refresh, and scopes connections per USER rather than per
     * account. We store nothing. The page merges the two lists for display only.
     *
     * Errors carry a machine-readable `code` (see fetchLocal) — branch on
     * PROVIDER_UNREACHABLE / UPSTREAM_UNREACHABLE rather than on the message, so
     * "could not ask" is never rendered as "nothing connected".
     */
    apps: {
      list(): Promise<{
        enabled: boolean;
        reason?: string;
        /**
         * false = we could not fetch toolkit metadata at all, so null logos
         * mean "unknown", not "this toolkit has none". Without this a blank
         * card is ambiguous.
         */
        catalog_available?: boolean;
        available: Array<{
          slug: string;
          connected: boolean;
          /** Presentation metadata from Composio; null when unavailable. */
          name?: string | null;
          logo?: string | null;
          description?: string | null;
          /** 0 means connecting this toolkit buys the user nothing. */
          tools_count?: number | null;
          /**
           * Whether Composio's catalog has this slug at all. false = it cannot
           * be connected, full stop. null = the catalog did not load, so we do
           * not know. Replaced a derived `connectable` flag that was wrong 5
           * times out of 8 against a real run.
           */
          in_catalog?: boolean | null;
        }>;
        connected: Array<{ slug: string; connected_at: string }>;
      }> {
        return fetchLocal('/api/integrations/apps');
      },

      connect(slug: string): Promise<{ url?: string; slug: string; already_connected?: boolean }> {
        return fetchLocal(`/api/integrations/apps/${encodeURIComponent(slug)}/connect`, {
          method: 'POST',
        });
      },

      disconnect(slug: string): Promise<{ ok: boolean; slug: string; removed: number }> {
        return fetchLocal(`/api/integrations/apps/${encodeURIComponent(slug)}`, {
          method: 'DELETE',
        });
      },
    },

    authorize(provider: 'google_drive' | 'dropbox' | 'google_calendar'): Promise<{ authorize_url: string; state: string }> {
      return fetchLocal(`/api/integrations/authorize/${provider}`);
    },

    get(id: string): Promise<{
      id: string;
      provider: 'google_drive' | 'dropbox' | 'google_calendar';
      account_label: string;
      status: 'connected' | 'disconnected' | 'needs_reauth' | 'error';
      attachments: Array<{
        id: string;
        agent_id: string;
        kind: 'folder' | 'file' | 'calendar';
        resource_id: string;
        resource_label: string | null;
        attached_at: string;
      }>;
    }> {
      return fetchLocal(`/api/integrations/${id}`);
    },

    disconnect(id: string): Promise<void> {
      return fetchLocal(`/api/integrations/${id}`, { method: 'DELETE' });
    },

    attach(id: string, body: {
      agent_id: string;
      resource_ids: string[];
      kind: 'folder' | 'file' | 'calendar';
      resource_labels?: Record<string, string>;
    }): Promise<{ attachments: Array<{ id: string; resource_id: string; resource_label: string | null }> }> {
      return fetchLocal(`/api/integrations/${id}/attachments`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    detach(integrationId: string, attachmentId: string): Promise<void> {
      return fetchLocal(`/api/integrations/${integrationId}/attachments/${attachmentId}`, {
        method: 'DELETE',
      });
    },

    // Drive-specific reads
    driveChildren(id: string, parentId: string = 'root', pageToken?: string): Promise<{
      items: Array<{
        id: string;
        name: string;
        mime_type: string;
        modified_time: string | null;
        size_bytes: number | null;
        is_folder: boolean;
      }>;
      next_page_token: string | null;
    }> {
      const qs = new URLSearchParams({ parent_id: parentId });
      if (pageToken) qs.set('page_token', pageToken);
      return fetchLocal(`/api/integrations/${id}/drive/children?${qs.toString()}`);
    },
  },

  // ── CEO Sessions (Next.js proxy → workspace-main, Bearer-authed) ─────────
  ceoSession: {
    generate(type?: string): Promise<CeoSession> {
      return fetchLocal('/api/ceo-sessions/generate', {
        method: 'POST',
        body: JSON.stringify({ type }),
      });
    },

    get(id: string): Promise<CeoSession> {
      return fetchLocal(`/api/ceo-sessions/${id}`);
    },

    list(): Promise<CeoSession[]> {
      return fetchLocal('/api/ceo-sessions');
    },
  },

  // ── Org Chart (local → Turso) ────────────────────────────────────────────
  orgChart: {
    tree(): Promise<unknown> {
      return fetchLocal('/api/org-chart/tree');
    },
    // Reparenting goes through the orgMembers client only. The agent-only
    // legacy wrapper (PATCH /api/agents/:id/reports-to → hub PUT
    // /api/org-chart/:id/reports-to) had no callers and was deleted (PR #198).
  },

  // ── Org members (humans on the org chart; agents live in agent_definitions) ─
  // Reads come back inside orgChart.tree() — this client is writes only.
  // Spec: 2026-05-04-org-tools-and-humans-migration-design.md
  orgMembers: {
    create(input: {
      name: string;
      role?: string;
      email?: string | null;
      reports_to?: string | null;
    }): Promise<{ member: OrgMemberDto }> {
      return fetchLocal('/api/org-members', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },

    update(
      id: string,
      patch: {
        name?: string;
        role?: string;
        email?: string | null;
        position_x?: number | null;
        position_y?: number | null;
      },
    ): Promise<{ member: OrgMemberDto }> {
      return fetchLocal(`/api/org-members/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    },

    remove(id: string): Promise<{ ok: true; orphaned_count: number }> {
      return fetchLocal(`/api/org-members/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
    },

    /**
     * Reparent any member — agent or human, under an agent or a human.
     * `parentId: null` makes it a root. This is the single reparent path for
     * the whole chart so cycle detection sees chains that cross kinds.
     */
    setReportsTo(
      memberId: string,
      parentId: string | null,
    ): Promise<{ ok: true; member_id: string; parent_id: string | null; kind: 'agent' | 'human' }> {
      return fetchLocal(`/api/org-members/${encodeURIComponent(memberId)}/reports-to`, {
        method: 'PATCH',
        body: JSON.stringify({ parent_id: parentId }),
      });
    },

    /** One-time migration of this device's localStorage humans. */
    importFromDevice(
      members: Array<{ id?: string; name: string; role?: string; reports_to?: string | null }>,
    ): Promise<{ ok: true; imported: number; rewired_agents: number; id_map: Record<string, string> }> {
      return fetchLocal('/api/org-members/import', {
        method: 'POST',
        body: JSON.stringify({ members }),
      });
    },
  },

  // ── Rooms (local → Turso for reads; proxied to workspace-main for writes) ─
  rooms: {
    list(type?: string): Promise<Room[]> {
      const qs = type ? `?type=${encodeURIComponent(type)}` : '';
      return fetchLocal(`/api/rooms${qs}`);
    },

    messages(roomId: string): Promise<RoomMessage[]> {
      return fetchLocal(`/api/rooms/${roomId}/messages`);
    },

    // Create a standing-team or war-room. Proxies to workspace-main's
    // /api/rooms/standing-team or /api/rooms/war-room based on `type`.
    // Spec: 2026-05-03-rooms-real-time-design.md §3.
    create(payload: {
      type: 'standing_team' | 'war_room';
      name: string;
      vertical?: string;
      leadAgentId?: string;
      goalId?: string;
      agentIds?: string[];
    }): Promise<{ room: Room }> {
      return fetchLocal(`/api/rooms`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    // Send a user-authored message to a room. Server attributes via the
    // JWT (req.accountUserId), so this body never carries the user id.
    send(
      roomId: string,
      content: string,
      messageType?: string,
      inReplyTo?: string,
    ): Promise<{ message: RoomMessage }> {
      return fetchLocal(`/api/rooms/${roomId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          content,
          ...(messageType ? { messageType } : {}),
          ...(inReplyTo ? { inReplyTo } : {}),
        }),
      });
    },

    // ── Membership management (Phase 1 active-collab — spec
    //   2026-05-03-rooms-active-collaboration-design.md) ────────────────
    // Initial fetch is via members(); live updates arrive on the
    // room-member-changed WS frame after that.
    members(roomId: string): Promise<{ members: import('./types').RoomMember[] }> {
      return fetchLocal(`/api/rooms/${roomId}/agents`);
    },
    addMember(roomId: string, agentId: string): Promise<{ success: true; agentId: string }> {
      return fetchLocal(`/api/rooms/${roomId}/agents`, {
        method: 'POST',
        body: JSON.stringify({ agentId }),
      });
    },
    removeMember(roomId: string, agentId: string): Promise<{ success: true; agentId: string }> {
      return fetchLocal(`/api/rooms/${roomId}/agents/${agentId}`, {
        method: 'DELETE',
      });
    },
    // Set (or clear) the room's lead agent. `null` clears — an explicit
    // value, not an omission, so "no lead" is a chosen state rather than a
    // dropped field. The lead must already be a live member of the room;
    // that is enforced upstream in one statement, so a concurrent member
    // removal can never leave a non-member lead installed.
    // Spec: ctrl-a_workspace-main/docs/superpowers/specs/2026-08-21-room-lead-picker-design.md §1, §3.
    setLead(roomId: string, leadAgentId: string | null): Promise<{ ok: true; id: string; lead_agent_id: string | null }> {
      return fetchLocal(`/api/rooms/${roomId}/lead`, {
        method: 'PATCH',
        body: JSON.stringify({ lead_agent_id: leadAgentId }),
      });
    },
    // Soft-archive a room. Spec:
    // ctrl-a_workspace-main/docs/superpowers/specs/2026-05-04-room-chat-archive-and-projects-contacts-design.md.
    archive(roomId: string): Promise<{ ok: true; id: string; archived_at: string }> {
      return fetchLocal(`/api/rooms/${roomId}`, { method: 'DELETE' });
    },
  },

  // ── Platform agent catalog ──────────────────────────────────────────────
  // The single source for "which agents can this account hire". Curated and
  // tier-aware; see CatalogAgent in types.ts for why the raw agent_definitions
  // read it replaced could not do this job.
  platform: {
    agentCatalog(): Promise<{ agents: CatalogAgent[]; current_tier: string }> {
      return fetchLocal('/api/platform/agents/catalog');
    },
  },

  // ── Marketplace (local → Turso) ──────────────────────────────────────────
  marketplace: {
    skills(params?: { category?: string; q?: string }): Promise<MarketplaceSkill[]> {
      const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
      return fetchLocal(`/api/marketplace/skills${qs}`);
    },
  },

  // ── Account Agents (hire/remove) ───────────────────────────────────────
  accountAgents: {
    hire(agentId: string): Promise<{ success: boolean; action: string; agentName?: string }> {
      return fetchLocal('/api/account-agents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId }),
      });
    },

    remove(agentId: string): Promise<{ success: boolean; action: string }> {
      return fetchLocal(`/api/account-agents?agentId=${agentId}`, { method: 'DELETE' });
    },
  },

  // ── Starter Kits (local → Turso) ────────────────────────────────────────
  starterKits: {
    list(filters?: Record<string, string>): Promise<StarterKit[]> {
      const qs = filters ? '?' + new URLSearchParams(filters).toString() : '';
      return fetchLocal(`/api/starter-kits${qs}`);
    },

    preview(id: string): Promise<unknown> {
      return fetchLocal(`/api/starter-kits/${id}/preview`);
    },

    apply(id: string, options: unknown): Promise<unknown> {
      return fetchLocal(`/api/starter-kits/${id}/apply`, {
        method: 'POST',
        body: JSON.stringify(options),
      });
    },
  },

  // ── Account lifecycle ────────────────────────────────────────────────────
  account: {
    /** Permanently delete the account. Requires admin PIN elevation (owner only). */
    delete(): Promise<unknown> {
      return fetchLocal('/api/account', { method: 'DELETE' });
    },
  },

  // ── Settings (local → Turso) ─────────────────────────────────────────────
  settings: {
    account(): Promise<Account> {
      return fetchLocal('/api/settings/account');
    },

    contacts(): Promise<AccountContact[]> {
      return fetchLocal('/api/settings/contacts');
    },

    update(data: Partial<Account>): Promise<unknown> {
      return fetchLocal('/api/settings/account', {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },
  },

  // ── Audit Trail (local proxy → workspace-main) ──────────────────────────
  audit: {
    list(limit = 100): Promise<AuditEntry[]> {
      return fetchLocal(`/api/account/audit?limit=${limit}`);
    },
  },

  // ── Soul Docs (proxy → workspace-main) ───────────────────────────────────
  soulDocs: {
    list(): Promise<SoulDoc[]> {
      return fetchLocal('/api/soul-docs');
    },

    get(agentId: string): Promise<{ soul: SoulDoc | null; history: SoulDocHistoryEntry[] }> {
      return fetchLocal(`/api/soul-docs/${agentId}`);
    },

    init(
      agentId: string,
      data: { personality?: string; tone?: string; identity?: string },
    ): Promise<SoulDoc> {
      return fetchLocal(`/api/soul-docs/${agentId}/init`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    update(
      agentId: string,
      data: {
        personality?: string;
        tone?: string;
        preferences?: string;
        learned_behaviors?: string;
        guardrails?: string;
        identity_statement?: string;
        reason: string;
      },
    ): Promise<SoulDoc> {
      return fetchLocal(`/api/soul-docs/${agentId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    prompt(agentId: string): Promise<{ prompt: string }> {
      return fetchLocal(`/api/soul-docs/${agentId}/prompt`);
    },
  },

  // ── Guardrails (proxy → workspace-main) ─────────────────────────────────
  guardrails: {
    list(params?: { scope?: GuardrailScope; scopeId?: string }): Promise<Guardrail[]> {
      const qs = params
        ? '?' + new URLSearchParams(params as Record<string, string>).toString()
        : '';
      return fetchLocal(`/api/guardrails${qs}`);
    },

    create(data: {
      scope: GuardrailScope;
      scopeId: string;
      ruleType: GuardrailRuleType;
      name: string;
      description: string;
      ruleJson: string;
      severity: GuardrailSeverity;
    }): Promise<Guardrail> {
      return fetchLocal('/api/guardrails', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    delete(id: string): Promise<{ success: boolean }> {
      return fetchLocal(`/api/guardrails/${id}`, { method: 'DELETE' });
    },
  },

  // ── Dashboard (local → Turso) ────────────────────────────────────────────
  dashboard: {
    stats(): Promise<DashboardStats> {
      return fetchLocal('/api/dashboard/stats');
    },
  },

  // ── Analytics (local → Turso) ──────────────────────────────────────────
  analytics: {
    get(range?: string): Promise<AnalyticsData> {
      const qs = range ? `?range=${range}` : '';
      return fetchLocal(`/api/analytics${qs}`);
    },
  },

  // ── Calendar (local → Turso) ───────────────────────────────────────────
  calendar: {
    // `signal` is forwarded into fetch so an abort actually CANCELS the request.
    // Without it the calendar could only check `signal.aborted` after the
    // response had already arrived, which cancelled nothing: paging quickly
    // through months left every request running to completion.
    events(params?: { start?: string; end?: string; source?: string }, signal?: AbortSignal): Promise<CalendarEvent[]> {
      const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
      return fetchLocal(`/api/calendar/events${qs}`, signal ? { signal } : undefined);
    },

    createEvent(data: Partial<CalendarEvent>): Promise<CalendarEvent> {
      return fetchLocal('/api/calendar/events', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    updateEvent(id: string, data: Partial<CalendarEvent>): Promise<CalendarEvent> {
      return fetchLocal(`/api/calendar/events/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    deleteEvent(id: string): Promise<{ success: boolean }> {
      return fetchLocal(`/api/calendar/events/${id}`, { method: 'DELETE' });
    },

    // Saved focus views. Built-in views are constants in
    // src/lib/calendar/built-in-views.ts and never round-trip through here, so
    // an empty list from list() is a normal state, not an error.
    // Spec: docs/superpowers/specs/2026-08-29-calendar-views-design.md
    views: {
      list(): Promise<FocusView[]> {
        return fetchLocal('/api/calendar/views');
      },

      create(data: {
        name: string;
        sources: CalendarItemKind[];
        filters?: Record<string, unknown>;
        icon?: string | null;
        /** Omit or false to keep the view personal - the default. */
        shared?: boolean;
      }): Promise<FocusView> {
        return fetchLocal('/api/calendar/views', {
          method: 'POST',
          body: JSON.stringify(data),
        });
      },

      update(
        id: string,
        data: Partial<{
          name: string;
          sources: CalendarItemKind[];
          filters: Record<string, unknown>;
          icon: string | null;
          position: number;
          shared: boolean;
        }>,
      ): Promise<FocusView> {
        return fetchLocal(`/api/calendar/views/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        });
      },

      remove(id: string): Promise<{ success: boolean }> {
        return fetchLocal(`/api/calendar/views/${id}`, { method: 'DELETE' });
      },
    },

    // Windowed CRM rows for the calendar's CRM source. One call rather than
    // composing /api/crm/activities and /api/crm/deals, so the source has one
    // failure mode instead of two.
    crmItems(params: { start: string; end: string }, signal?: AbortSignal): Promise<{
      activities: CrmActivity[];
      deals: CrmDeal[];
    }> {
      const qs = '?' + new URLSearchParams(params).toString();
      return fetchLocal(`/api/calendar/crm-items${qs}`, signal ? { signal } : undefined);
    },

    connections(): Promise<CalendarConnection[]> {
      return fetchLocal('/api/calendar/connections');
    },

    createConnection(data: { provider: string; label?: string; color?: string }): Promise<CalendarConnection> {
      return fetchLocal('/api/calendar/connections', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    deleteConnection(id: string): Promise<{ success: boolean }> {
      return fetchLocal(`/api/calendar/connections?id=${id}`, { method: 'DELETE' });
    },
  },

  // ── Documents / Knowledge (local → Turso) ──────────────────────────────
  documents: {
    list(search?: string): Promise<Document[]> {
      const qs = search ? `?q=${encodeURIComponent(search)}` : '';
      return fetchLocal(`/api/documents${qs}`);
    },

    /** @deprecated Use `api.accountKnowledge.createSource` for the new ingestion flow.
     *  Retained for legacy catalog code paths that still write metadata-only rows. */
    create(data: { name: string; doc_type?: string; mime_type?: string; size_bytes?: number }): Promise<Document> {
      return fetchLocal('/api/documents', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  },

  // ── Account Knowledge (2026-04-26 ingestion spec) ──────────────────────
  accountKnowledge: {
    listSources(params?: { status?: string; q?: string; projectId?: string }): Promise<KnowledgeSource[]> {
      const sp = new URLSearchParams();
      if (params?.status) sp.set('status', params.status);
      if (params?.q) sp.set('q', params.q);
      if (params?.projectId) sp.set('project_id', params.projectId);
      const qs = sp.toString() ? `?${sp.toString()}` : '';
      return fetchLocal(`/api/account/knowledge/sources${qs}`);
    },

    getSource(id: string): Promise<KnowledgeSource> {
      return fetchLocal(`/api/account/knowledge/sources/${id}`);
    },

    async createSource(
      input:
        | { kind: 'file'; file: File; projectId?: string }
        | { kind: 'text'; text: string; filename?: string; projectId?: string }
        | { kind: 'url'; url: string; title?: string; projectId?: string },
    ): Promise<KnowledgeSource> {
      const token =
        typeof window !== 'undefined'
          ? localStorage.getItem('ctrl-a-token')
          : null;

      if (input.kind === 'file') {
        const form = new FormData();
        form.append('file', input.file);
        if (input.projectId) form.append('project_id', input.projectId);
        const res = await fetch('/api/account/knowledge/sources', {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: form,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: 'Upload failed' }));
          throw new Error((err as { error?: string }).error || `Upload failed: ${res.status}`);
        }
        return res.json();
      }

      const body =
        input.kind === 'text'
          ? { text: input.text, filename: input.filename, project_id: input.projectId }
          : { url: input.url, title: input.title, project_id: input.projectId };
      return fetchLocal('/api/account/knowledge/sources', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    deleteSource(id: string): Promise<{ ok: boolean }> {
      return fetchLocal(`/api/account/knowledge/sources/${id}`, {
        method: 'DELETE',
      });
    },

    rebuild(id: string): Promise<{ ok: boolean }> {
      return fetchLocal(`/api/account/knowledge/sources/${id}/rebuild`, {
        method: 'POST',
      });
    },

    attachToAgent(id: string, agentId: string): Promise<{ agent_source_id: string }> {
      return fetchLocal(`/api/account/knowledge/sources/${id}/attach`, {
        method: 'POST',
        body: JSON.stringify({ agent_id: agentId }),
      });
    },
  },

  // ── Account Security Settings (2026-05-09 spec) ─────────────────────────
  // Persistent storage for the /settings Safety Net Setup card. Owner+admin
  // only for PATCH; any authed account user can GET. See
  // docs/superpowers/specs/2026-05-09-account-security-settings-design.md
  accountSecurity: {
    get(): Promise<{ settings: import('./security-settings').SecuritySettings; defaults: import('./security-settings').SecuritySettings }> {
      return fetchLocal('/api/account/security/settings');
    },
    update(
      patch: Partial<import('./security-settings').SecuritySettings>,
    ): Promise<{ settings: import('./security-settings').SecuritySettings }> {
      return fetchLocal('/api/account/security/settings', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    },
  },

  // ── Workflows v2 (proxy → workspace-main authored workflows) ──────────
  // The retrospective delegation-chain view (legacy) now lives under
  // `api.workflows.activity()` and hits `/api/workflows/activity`.
  //
  // ENVELOPE CONTRACT: workspace-main consistently wraps responses, e.g.
  //   GET  /api/workflows           → { workflows: Workflow[] }
  //   POST /api/workflows           → { workflow: Workflow }
  //   GET  /api/workflows/:id       → { workflow: Workflow }
  //   POST /api/workflows/.../runs  → { run: WorkflowRun }
  //   GET  /api/workflows/templates → { templates: WorkflowTemplate[] }
  // Each method below unwraps once at the API boundary so callers can use
  // flat types. Pre-2026-05-07 the client cast the raw envelope to the
  // flat type, which silently broke create (wf.id was undefined →
  // redirect to /workflows/undefined → 404) and made list/templates look
  // like empty state.
  workflows: {
    async list(): Promise<Workflow[]> {
      const data = await fetchLocal<{ workflows: Workflow[] } | Workflow[]>(
        '/api/workflows',
      );
      // Tolerate both the wrapped envelope (production) and a bare array
      // (dev-mode 401 fallback in src/app/api/workflows/route.ts).
      if (Array.isArray(data)) return data;
      return Array.isArray(data?.workflows) ? data.workflows : [];
    },

    async get(id: string): Promise<Workflow> {
      const data = await fetchLocal<{ workflow: Workflow }>(`/api/workflows/${id}`);
      return data.workflow;
    },

    async create(req: CreateWorkflowRequest): Promise<Workflow> {
      const data = await fetchLocal<{ workflow: Workflow }>('/api/workflows', {
        method: 'POST',
        body: JSON.stringify(req),
      });
      return data.workflow;
    },

    async update(id: string, patch: Partial<Workflow>): Promise<Workflow> {
      const data = await fetchLocal<{ workflow: Workflow }>(`/api/workflows/${id}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
      return data.workflow;
    },

    activate(
      id: string,
    ): Promise<{ ok: true; workflow: Workflow; warnings?: WorkflowValidationError[] }> {
      // Backend already returns `{ ok, workflow, warnings }` flat.
      return fetchLocal(`/api/workflows/${id}/activate`, {
        method: 'POST',
      });
    },

    archive(id: string): Promise<{ ok: true }> {
      return fetchLocal(`/api/workflows/${id}/archive`, {
        method: 'POST',
      });
    },

    /**
     * Active → draft. Unregisters cron/webhook so nothing can START a new run.
     * IN-FLIGHT RUNS CONTINUE — the engine snapshots the graph at run start, so
     * a run already going is unaffected. The confirm copy must say so, or
     * "deactivated" reads as "stopped".
     */
    deactivate(id: string): Promise<{ ok: true; workflow: Workflow }> {
      return fetchLocal(`/api/workflows/${id}/deactivate`, {
        method: 'POST',
      });
    },

    /**
     * Archived → DRAFT, never straight to active: the graph has not been
     * validated since archiving, and going live would skip the /activate gate.
     */
    unarchive(id: string): Promise<{ ok: true; workflow: Workflow }> {
      return fetchLocal(`/api/workflows/${id}/unarchive`, {
        method: 'POST',
      });
    },

    async startRun(id: string, body?: StartRunRequest): Promise<WorkflowRun> {
      const data = await fetchLocal<{ run: WorkflowRun }>(`/api/workflows/${id}/runs`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      });
      return data.run;
    },

    getRun(runId: string): Promise<{ run: WorkflowRun; nodes: WorkflowRunNode[] }> {
      // Backend already returns `{ run, nodes }` flat.
      return fetchLocal(`/api/workflows/runs/${runId}`);
    },

    cancelRun(runId: string): Promise<{ ok: true }> {
      return fetchLocal(`/api/workflows/runs/${runId}/cancel`, {
        method: 'POST',
      });
    },

    resumeRun(
      runId: string,
      body: ResumeRunRequest,
    ): Promise<{ run: WorkflowRun }> {
      // Backend returns `{ run }`; we keep the wrapper here because the
      // declared return type already exposes it.
      return fetchLocal(`/api/workflows/runs/${runId}/resume`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async templates(vertical?: string): Promise<WorkflowTemplate[]> {
      const qs = vertical ? `?vertical=${encodeURIComponent(vertical)}` : '';
      const data = await fetchLocal<
        { templates: WorkflowTemplate[] } | WorkflowTemplate[]
      >(`/api/workflows/templates${qs}`);
      if (Array.isArray(data)) return data;
      return Array.isArray(data?.templates) ? data.templates : [];
    },

    async clone(templateId: string, name?: string): Promise<Workflow> {
      const data = await fetchLocal<{ workflow: Workflow }>(
        `/api/workflows/templates/${templateId}/clone`,
        {
          method: 'POST',
          body: JSON.stringify(name ? { name } : {}),
        },
      );
      return data.workflow;
    },

    /**
     * Activity ledger — runs bucketed by status for the "Ledger" tab on
     * `/workflows`. Distinct from `activity()` below (which is the legacy
     * delegation-chain view under the "Activity" tab and reads
     * `agent_wakeup_queue`/`actions`, not `workflow_runs`).
     *
     * `active` merges queued/running/paused_on_gate/paused_on_approval;
     * `failed` is status='failed' only. See
     * `src/app/api/workflows/runs/summary/route.ts` for why this is a
     * dedicated route rather than a raw `/api/workflows/runs?status=` call.
     *
     * Spec: docs/superpowers/specs/2026-09-20-workflow-activity-ledger-ui-design.md
     */
    /**
     * `degraded` is only populated for status='failed': runs that reported
     * completed while a step never validated. Kept separate from `runs` so
     * the UI never renders a run that DID finish as a plain "Failed" row.
     */
    async runsSummary(
      status: 'active' | 'failed',
    ): Promise<{ runs: WorkflowRun[]; degraded: DegradedRun[] }> {
      const data = await fetchLocal<
        { runs?: WorkflowRun[]; degraded?: DegradedRun[] } | WorkflowRun[]
      >(`/api/workflows/runs/summary?status=${encodeURIComponent(status)}`);
      if (Array.isArray(data)) return { runs: data, degraded: [] };
      return {
        runs: Array.isArray(data?.runs) ? data.runs : [],
        degraded: Array.isArray(data?.degraded) ? data.degraded : [],
      };
    },

    /**
     * Retrospective delegation-chain view (legacy behavior of
     * `/api/workflows`). Backed by `agent_wakeup_queue` + `actions`.
     */
    async activity(): Promise<unknown[]> {
      const data = await fetchLocal<{ activity: unknown[] } | unknown[]>(
        '/api/workflows/activity',
      );
      if (Array.isArray(data)) return data;
      return Array.isArray((data as { activity?: unknown[] })?.activity)
        ? (data as { activity: unknown[] }).activity
        : [];
    },
  },

  // ── Vault (local → Turso) ─────────────────────────────────────────────
  vault: {
    list(): Promise<Record<string, unknown>[]> {
      return fetchLocal('/api/vault');
    },

    create(data: {
      name: string;
      value: string;
      category: string;
      scope: string;
    }): Promise<Record<string, unknown>> {
      return fetchLocal('/api/vault', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    update(
      id: string,
      data: { name?: string; value?: string; category?: string; scope?: string },
    ): Promise<void> {
      return fetchLocal(`/api/vault/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    delete(id: string): Promise<void> {
      return fetchLocal(`/api/vault/${id}`, { method: 'DELETE' });
    },
  },

  // ── CRM (local → Turso) ────────────────────────────────────────────────
  crm: {
    contacts(params?: { search?: string; type?: string; tag?: string; project_id?: string }): Promise<CrmContact[]> {
      const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
      return fetchLocal(`/api/crm/contacts${qs}`);
    },

    getContact(id: string): Promise<CrmContact> {
      return fetchLocal(`/api/crm/contacts/${id}`);
    },

    createContact(data: Partial<CrmContact>): Promise<CrmContact> {
      return fetchLocal('/api/crm/contacts', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    updateContact(id: string, data: Partial<CrmContact>): Promise<CrmContact> {
      return fetchLocal(`/api/crm/contacts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    deleteContact(id: string): Promise<{ success: boolean }> {
      return fetchLocal(`/api/crm/contacts/${id}`, { method: 'DELETE' });
    },

    deals(params?: { pipeline_id?: string; stage?: string; status?: string }): Promise<CrmDeal[]> {
      const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
      return fetchLocal(`/api/crm/deals${qs}`);
    },

    createDeal(data: Partial<CrmDeal>): Promise<CrmDeal> {
      return fetchLocal('/api/crm/deals', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    updateDeal(id: string, data: Partial<CrmDeal>): Promise<CrmDeal> {
      return fetchLocal(`/api/crm/deals/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    deleteDeal(id: string): Promise<{ success: boolean }> {
      return fetchLocal(`/api/crm/deals/${id}`, { method: 'DELETE' });
    },

    pipelines(): Promise<CrmPipeline[]> {
      return fetchLocal('/api/crm/pipelines');
    },

    createPipeline(data: { name: string; stages_json?: string; is_default?: boolean }): Promise<CrmPipeline> {
      return fetchLocal('/api/crm/pipelines', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    activities(params?: { contact_id?: string; deal_id?: string; type?: string }): Promise<CrmActivity[]> {
      const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
      return fetchLocal(`/api/crm/activities${qs}`);
    },

    createActivity(data: Partial<CrmActivity>): Promise<CrmActivity> {
      return fetchLocal('/api/crm/activities', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  },

  // ── Leads (local → Turso) ─────────────────────────────────────────────
  leads: {
    list(params?: { status?: LeadInboxStatus; source?: string; q?: string; limit?: number; cursor?: string }): Promise<{ rows: LeadInboxRow[] }> {
      // Drop undefined values before serializing — URLSearchParams stringifies
      // them to the literal "undefined", which the route would treat as a
      // filter value (e.g. source=undefined → no rows match).
      const entries = params
        ? Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
        : [];
      const qs = entries.length > 0
        ? '?' + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()
        : '';
      return fetchLocal(`/api/leads${qs}`);
    },

    get(id: string): Promise<LeadInboxRow> {
      return fetchLocal(`/api/leads/${id}`);
    },

    create(data: LeadInboxInput): Promise<{ id: string }> {
      return fetchLocal('/api/leads', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    update(id: string, data: Partial<Pick<LeadInboxRow, 'email' | 'phone' | 'name' | 'company' | 'notes'>>): Promise<LeadInboxRow> {
      return fetchLocal(`/api/leads/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    discard(id: string): Promise<void> {
      return fetchLocal(`/api/leads/${id}`, { method: 'DELETE' });
    },

    /**
     * Promote a lead to a contact. The endpoint can return 409 with a typed
     * body (`duplicate_contact` w/ `existing_contact_id`, or `already_promoted`)
     * that the UI needs to surface (dupe modal). `fetchLocal` strips the body,
     * so this method does its own fetch and throws an Error decorated with
     * `status` + `body` so callers can branch on the error envelope.
     */
    async promote(
      id: string,
      args?: { merge?: boolean },
    ): Promise<{ contact_id: string; inbox_id: string }> {
      const token =
        typeof window !== 'undefined'
          ? localStorage.getItem('ctrl-a-token')
          : null;
      const res = await fetch(`/api/leads/${id}/promote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(args ?? {}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const e = new Error(
          (body as { error?: string }).error || `Promote failed (${res.status})`,
        ) as Error & { status?: number; body?: Record<string, unknown> };
        e.status = res.status;
        e.body = body as Record<string, unknown>;
        throw e;
      }
      return body as { contact_id: string; inbox_id: string };
    },

    bulk(args: { ids: string[]; action: 'archive'|'discard'|'promote'; merge?: boolean }): Promise<{ succeeded: string[]; failed: { id: string; reason: string }[] }> {
      return fetchLocal('/api/leads/bulk', {
        method: 'POST',
        body: JSON.stringify(args),
      });
    },

    importCsv(file: File): Promise<{ total: number; inserted: number; skipped: number; errors: { row: number; reason: string }[] }> {
      const token = typeof window !== 'undefined' ? localStorage.getItem('ctrl-a-token') : null;
      const form = new FormData();
      form.append('file', file);
      return fetch('/api/leads/import-csv', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
      }).then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          const e = new Error(body.error || `Import failed (${res.status})`) as Error & { status?: number; body?: typeof body };
          e.status = res.status;
          e.body = body;
          throw e;
        }
        return body;
      });
    },
  },

  // ── Public Lead Keys (local → Turso) ──────────────────────────────
  leadKeys: {
    list(): Promise<{ keys: Array<{ id: string; label: string; daily_cap: number; created_at: string; revoked_at: string | null; last_used_at: string | null }> }> {
      return fetchLocal('/api/account/lead-keys');
    },
    create(args: { label: string; daily_cap?: number }): Promise<{ id: string; label: string; daily_cap: number; secret: string }> {
      return fetchLocal('/api/account/lead-keys', {
        method: 'POST',
        body: JSON.stringify(args),
      });
    },
    revoke(id: string): Promise<{ success: boolean }> {
      return fetchLocal(`/api/account/lead-keys/${id}`, { method: 'DELETE' });
    },
  },

  // ── Console (local → Turso) ───────────────────────────────────────────
  console: {
    logs(): Promise<Record<string, unknown>[]> {
      return fetchLocal('/api/console/logs');
    },
  },

  // ── Deployments (local → Turso) ─────────────────────────────────────
  deployments: {
    list(): Promise<{ deployments: Record<string, unknown>[] }> {
      return fetchLocal('/api/deployments');
    },
  },

  // ── Plans (public — no auth required) ────────────────────────────────
  plans: {
    list(): Promise<Plan[]> {
      return fetchLocal('/api/plans');
    },
  },

  // ── Billing (local proxy → workspace-main) ───────────────────────────
  billing: {
    get(): Promise<AccountBilling> {
      return fetchLocal('/api/account/billing');
    },

    /** Free-trial state + usage, straight from the hub (which meters it).
     *  Hub spec 2026-09-26-free-tier-build-design.md. */
    freeTier(): Promise<FreeTierStatus> {
      return fetchInference<FreeTierStatus>('/api/account/free-tier');
    },

    subscribe(planId: string, billingCycle: 'monthly' | 'annual'): Promise<SubscribeResponse> {
      return fetchLocal('/api/account/billing/subscribe', {
        method: 'POST',
        body: JSON.stringify({ planId, billingCycle }),
      });
    },

    /** One-time Quickstart prepay (EARLY-* codes). Returns Stripe Checkout URL. */
    quickstartCheckout(promoCode: string): Promise<{ url: string; sessionId: string; orderId: string }> {
      return fetchLocal('/api/billing/quickstart-checkout', {
        method: 'POST',
        body: JSON.stringify({ promo_code: promoCode }),
      });
    },

    cancel(): Promise<{ success: boolean; cancel_at_period_end: boolean }> {
      return fetchLocal('/api/account/billing/cancel', { method: 'POST' });
    },

    usage(period?: string): Promise<UsageSummary> {
      const qs = period ? `?period=${encodeURIComponent(period)}` : '';
      return fetchLocal(`/api/account/usage${qs}`);
    },

    metered(): Promise<MeteredPricing[]> {
      return fetchLocal('/api/pricing/metered');
    },

    /** Account tier — Wave E (Tier Orchestration Model). */
    tier(): Promise<TierResponse> {
      return fetchLocal('/api/account/tier');
    },
  },

  // ── Access codes / paywall (Phase 2b signup+paywall) ─────────────────────
  accessCodes: {
    /** Is this account currently entitled to the platform? Open-list read. */
    entitlement(): Promise<{ entitled: boolean }> {
      return fetchLocal('/api/account/entitlement');
    },
    /** Redeem an owner or minted comp code onto the current account. */
    redeem(code: string): Promise<{ ok?: boolean; source?: string; tier?: string; plan_expires_at?: string; error?: string; reason?: string }> {
      return fetchLocal('/api/account/redeem-code', {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
    },
    /** Owner-only: list minted codes. */
    list(): Promise<{ codes: AccessCode[] }> {
      return fetchLocal('/api/admin/codes');
    },
    /** Owner-only: mint a code. */
    mint(input: { duration_days: 7 | 14 | 30; max_redemptions?: number; tier?: string; note?: string }): Promise<AccessCode & { ok: boolean }> {
      return fetchLocal('/api/admin/codes', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    /** Owner-only: revoke a code. */
    revoke(code: string): Promise<{ ok?: boolean; error?: string }> {
      return fetchLocal(`/api/admin/codes/${encodeURIComponent(code)}/revoke`, { method: 'POST' });
    },
  },

  // ── Artifacts (local → Turso) ──────────────────────────────────────
  artifacts: {
    list(): Promise<ArtifactRow[]> {
      return fetchLocal('/api/artifacts');
    },

    get(id: string): Promise<ArtifactRow> {
      return fetchLocal(`/api/artifacts/${id}`);
    },

    create(data: {
      project_name: string;
      project_type?: string;
      file_path: string;
      content?: string;
    }): Promise<ArtifactRow> {
      return fetchLocal('/api/artifacts', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    update(
      id: string,
      data: Partial<{ project_name: string; project_type: string; file_path: string; content: string }>,
    ): Promise<{ success: boolean }> {
      return fetchLocal(`/api/artifacts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    delete(id: string): Promise<{ success: boolean }> {
      return fetchLocal(`/api/artifacts/${id}`, { method: 'DELETE' });
    },
  },

  // ── Canvas (PR 3 — stub for PR 2 of 2026-07-14-canvas-primitive) ──────────
  // PR 2 lands UI (open-state module, /canvas page, chat-preview card); the
  // real hub routes GET /api/canvas and PATCH /api/canvas/:id/hidden ship in
  // PR 3. Until then these reject cleanly so components render a controlled
  // error rather than crashing on `undefined.fetch`.
  canvas: {
    /** Tenant's visible + hidden canvas artifacts. Proxy → hub `GET /api/canvas`. */
    list(): Promise<{ open: ArtifactRow[]; closed: ArtifactRow[] }> {
      return fetchLocal('/api/canvas');
    },

    /**
     * Hide or restore an artifact. Proxy → hub `PATCH /api/canvas/:id/hidden`.
     *
     * This is the writer whose absence made `hidden` a dead column: it was a
     * stub that threw, so nothing could ever set hidden=1 and every artifact
     * was permanently "open" to the agent's context injection.
     */
    setHidden(id: string, hidden: boolean): Promise<{ id: string; hidden: boolean }> {
      return fetchLocal(`/api/canvas/${encodeURIComponent(id)}/hidden`, {
        method: 'PATCH',
        body: JSON.stringify({ hidden }),
      });
    },

    /**
     * Persist board geometry. Proxy → hub `PATCH /api/canvas/:id/layout`.
     *
     * Geometry only: this never rewrites `content` or bumps `version`, so a drag
     * does not create an artifact revision. `z` is not accepted — pass
     * `bringToFront: true` and the server computes the new top.
     */
    setLayout(
      id: string,
      layout: { x?: number; y?: number; w?: number; h?: number; bringToFront?: boolean },
    ): Promise<ArtifactRow> {
      return fetchLocal(`/api/canvas/${encodeURIComponent(id)}/layout`, {
        method: 'PATCH',
        body: JSON.stringify(layout),
      });
    },
  },

  // ── Brand Kit (proxy → workspace-main) ──────────────────────────────────
  brand: {
    get(): Promise<import('./types').BrandKitResponse> {
      return fetchLocal('/api/account/brand');
    },

    patch(partial: Partial<import('./types').BrandIdentity>): Promise<import('./types').BrandIdentity> {
      return fetchLocal('/api/account/brand', {
        method: 'PATCH',
        body: JSON.stringify(partial),
      });
    },
  },

  // ── Triggers (proxy → workspace-main Express) ──────────────────────────
  triggers: {
    list(agentId: string): Promise<AgentTrigger[]> {
      return fetchLocal(`/api/triggers?agent_id=${encodeURIComponent(agentId)}`);
    },

    get(id: string): Promise<AgentTrigger> {
      return fetchLocal(`/api/triggers/${id}`);
    },

    create(data: {
      agent_id: string;
      trigger_type: string;
      trigger_config: string;
      action_type: string;
      action_config: string;
      priority?: number;
      enabled?: 0 | 1;
    }): Promise<AgentTrigger> {
      return fetchLocal('/api/triggers', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    update(id: string, data: Partial<AgentTrigger>): Promise<AgentTrigger> {
      return fetchLocal(`/api/triggers/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    delete(id: string): Promise<{ success: boolean }> {
      return fetchLocal(`/api/triggers/${id}`, { method: 'DELETE' });
    },
  },

  // ── Channels (proxy → workspace-main backend) ─────────────────────────
  channels: {
    list(): Promise<ChannelsResponse> {
      return fetchLocal('/api/channels');
    },

    create(data: {
      type: ChannelType;
      name: string;
      config: Record<string, unknown>;
      default_agent_id?: string | null;
      compliance_ack?: true;
    }): Promise<{ id: string; channel?: Channel | null; next_step?: 'compliance_ack' | null; warnings?: string[] }> {
      return fetchLocal('/api/channels', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    testInbound(data: {
      channelId: string;
      channelType: ChannelType;
      senderId: string;
      content: string;
    }): Promise<{ success: boolean; response?: string; error?: string }> {
      return fetchLocal('/api/channels/inbound', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    // SMS: `compliance_ack: true` may ride with the config, or alone (ack-only PATCH, hub #461).
    update(
      id: string,
      data: { config?: Record<string, unknown>; compliance_ack?: true },
    ): Promise<{ id: string; channel?: Channel | null; next_step?: 'compliance_ack' | null; warnings?: string[] }> {
      return fetchLocal(`/api/channels/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    // Tenant SMS lifecycle (hub U3 sms-channel-routes.ts; owner/admin). Any non-2xx
    // throws with the hub `code`; a 200 can still carry a soft outcome, so the panel
    // always reloads `list()` afterwards.
    smsWebhook(id: string, body: SmsWebhookBody): Promise<SmsWebhookResponse> {
      return fetchLocal(`/api/channels/${encodeURIComponent(id)}/sms/webhook`, { method: 'POST', body: JSON.stringify(body) });
    },
    smsVerify(id: string, body: SmsVerifyBody = {}): Promise<SmsVerifyResponse> {
      return fetchLocal(`/api/channels/${encodeURIComponent(id)}/sms/verify`, { method: 'POST', body: JSON.stringify(body) });
    },
    smsTestStatus(id: string): Promise<SmsTestStatusResponse> {
      return fetchLocal(`/api/channels/${encodeURIComponent(id)}/sms/test-status`, { method: 'POST', body: '{}' });
    },
    smsDisconnect(id: string): Promise<SmsDisconnectResponse> {
      return fetchLocal(`/api/channels/${encodeURIComponent(id)}/sms/disconnect`, { method: 'POST', body: '{}' });
    },

    activate(
      id: string,
    ): Promise<
      | { webhook_url: string; activated_at: string; bot_username: string | null }
      | { error: string }
    > {
      return fetchLocal(`/api/channels/${id}/activate`, { method: 'POST' });
    },
  },

  // ── Channel identity binding (unified conversations) ──────────────────
  channelIdentity: {
    status(): Promise<{ telegram: string | null; phone: string | null; email: string | null }> {
      return fetchLocal('/api/account/channels/status');
    },

    link(data: {
      channel_type: 'telegram' | 'phone' | 'email';
      identity: string;
    }): Promise<
      | { pending_id: string; expires_at: string; message: string }
      | { error: string }
    > {
      return fetchLocal('/api/account/channels/link', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    verify(data: {
      pending_id: string;
      code: string;
    }): Promise<
      | { ok: true; channel_type: string; identity: string }
      | { error: string; attempts_remaining?: number }
    > {
      return fetchLocal('/api/account/channels/verify', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    unlink(channel_type: 'telegram' | 'phone' | 'email'): Promise<{ ok: true } | { error: string }> {
      return fetchLocal('/api/account/channels/binding', {
        method: 'DELETE',
        body: JSON.stringify({ channel_type }),
      });
    },
  },

  // ── Feedback (per 2026-05-13 spec — Feedback Collector agent) ──────────
  feedback: {
    list(filters?: {
      status?: string;
      source_app?: string;
      category?: string;
      sentiment?: string;
      limit?: number;
      offset?: number;
    }): Promise<{ feedback: FeedbackEvent[]; limit: number; offset: number }> {
      const qs = filters
        ? '?' +
          new URLSearchParams(
            Object.entries(filters)
              .filter(([, v]) => v !== undefined && v !== null && v !== '')
              .map(([k, v]) => [k, String(v)]),
          ).toString()
        : '';
      return fetchLocal(`/api/feedback${qs}`);
    },

    get(id: string, opts?: { includeRaw?: boolean }): Promise<FeedbackEvent> {
      // include_raw=true triggers a server-side audit log for the reveal.
      const qs = opts?.includeRaw ? '?include_raw=true' : '';
      return fetchLocal(`/api/feedback/${encodeURIComponent(id)}${qs}`);
    },

    updateStatus(
      id: string,
      status: FeedbackStatus,
      note?: string,
    ): Promise<Partial<FeedbackEvent>> {
      // Backend currently returns the full row, but may shrink to {id, status}
      // or {ok: true} in the future — caller must treat the response as partial.
      return fetchLocal(`/api/feedback/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ status, note }),
      });
    },

    report(input: {
      source: string;
      version?: string;
      accountId: string;
      userId?: string;
      text: string;
      metadata?: Record<string, unknown>;
    }): Promise<{ id: string; category: string; sentiment: string; status: string }> {
      return fetchLocal('/api/feedback/inbound', {
        method: 'POST',
        body: JSON.stringify({
          source_app: input.source,
          source_app_version: input.version ?? null,
          account_id: input.accountId,
          source_user_id: input.userId ?? null,
          text: input.text,
          metadata: input.metadata ?? {},
        }),
      });
    },
  },

  // ── SMS (proxy → workspace-main backend) ──────────────────────────────
  sms: {
    contacts(): Promise<SmsContact[]> {
      return fetchLocal('/api/sms/contacts');
    },

    createContact(data: {
      phone_number: string;
      agent_id?: string | null;
      contact_name?: string | null;
    }): Promise<SmsContact> {
      return fetchLocal('/api/sms/contacts', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    deleteContact(id: string): Promise<{ success: boolean }> {
      return fetchLocal(`/api/sms/contacts/${id}`, { method: 'DELETE' });
    },

    // Owner-marked opt-out for this account (hub U3 POST /sms/optouts; owner/admin).
    markOptedOut(phone: string): Promise<SmsOptoutResponse> {
      return fetchLocal('/api/sms/optouts', { method: 'POST', body: JSON.stringify({ phone }) });
    },

    // 200 = queued at Twilio, NOT delivered (hub /sms/test returns status 'queued').
    test(data: { to: string; message: string }): Promise<SmsTestSendResponse & { error?: string }> {
      return fetchLocal('/api/sms/test', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  },

  // ── Instance (proxy → workspace-main) ───────────────────────────────────
  instance: {
    get(): Promise<InstancePayload> {
      return fetchLocal('/api/instance');
    },

    setLabel(label: string): Promise<{ success: boolean; label: string | null }> {
      return fetchLocal('/api/instance', {
        method: 'PUT',
        body: JSON.stringify({ label }),
      });
    },
  },

  // ── Updates (proxy → workspace-main) ────────────────────────────────────
  updates: {
    status(): Promise<UpdateStatus> {
      return fetchLocal('/api/updates/status');
    },

    check(): Promise<UpdateStatus> {
      return fetchLocal('/api/updates/check', { method: 'POST' });
    },

    download(): Promise<UpdateStatus> {
      return fetchLocal('/api/updates/download', { method: 'POST' });
    },

    apply(): Promise<UpdateStatus> {
      return fetchLocal('/api/updates/apply', { method: 'POST' });
    },

    dismiss(): Promise<UpdateStatus> {
      return fetchLocal('/api/updates/dismiss', { method: 'POST' });
    },

    history(): Promise<UpdateHistoryEntry[]> {
      return fetchLocal('/api/updates/history');
    },
  },

  // ── Plugins (local → Turso; contract mirrors workspace-main plugin-routes) ─
  plugins: {
    /** List installed plugins; backend returns `{ plugins: [...] }`. */
    list(params?: { scope?: string; status?: string }): Promise<{ plugins: Plugin[] }> {
      const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
      return fetchLocal(`/api/plugins${qs}`);
    },

    /** Browse marketplace (available-to-install) plugins. */
    marketplace(params?: { q?: string; vertical?: string }): Promise<{ plugins: MarketplacePlugin[] }> {
      const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
      return fetchLocal(`/api/plugins/marketplace${qs}`);
    },

    /** Single plugin (full manifest_json included). */
    get(id: string): Promise<{ plugin: Plugin }> {
      return fetchLocal(`/api/plugins/${id}`);
    },

    /**
     * Install a plugin. Backend accepts a full manifest body; the Next proxy
     * accepts `{ plugin_id }`, looks the manifest up server-side, then forwards.
     */
    install(pluginId: string): Promise<{ id: string; status: string }> {
      return fetchLocal('/api/plugins', {
        method: 'POST',
        body: JSON.stringify({ plugin_id: pluginId }),
      });
    },

    uninstall(id: string): Promise<{ ok: boolean }> {
      return fetchLocal(`/api/plugins/${id}`, { method: 'DELETE' });
    },

    /** Current config values (secrets come back masked). */
    getConfig(id: string): Promise<{ config: PluginConfigValues }> {
      return fetchLocal(`/api/plugins/${id}/config`);
    },

    /** PUT full key→value blob; backend applies schema + sets status=configured. */
    setConfig(id: string, values: PluginConfigValues): Promise<{ ok: boolean }> {
      return fetchLocal(`/api/plugins/${id}/config`, {
        method: 'PUT',
        body: JSON.stringify(values),
      });
    },

    activate(id: string): Promise<{ ok: boolean; status: string }> {
      return fetchLocal(`/api/plugins/${id}/activate`, { method: 'POST' });
    },

    deactivate(id: string): Promise<{ ok: boolean; status: string }> {
      return fetchLocal(`/api/plugins/${id}/deactivate`, { method: 'POST' });
    },

    /** Agents this plugin is enabled for (returns rows from plugin_agents). */
    listAgents(pluginId: string): Promise<{ agents: PluginAgentBinding[] }> {
      return fetchLocal(`/api/plugins/${pluginId}/agents`);
    },

    /**
     * Toggle per-agent binding. Backend uses POST /agents/:agentId (enable)
     * and DELETE /agents/:agentId (disable).
     */
    setAgentEnabled(
      pluginId: string,
      agentId: string,
      enabled: boolean,
    ): Promise<{ ok: boolean }> {
      if (enabled) {
        return fetchLocal(`/api/plugins/${pluginId}/agents/${agentId}`, {
          method: 'POST',
          body: JSON.stringify({}),
        });
      }
      return fetchLocal(`/api/plugins/${pluginId}/agents/${agentId}`, {
        method: 'DELETE',
      });
    },
  },

  // ── Elevation (Admin PIN — local → Next.js API routes) ───────────────────
  elevation: {
    status(): Promise<ElevationStatus> {
      return fetchLocal('/api/elevation/status');
    },
    verify(pin: string, requireVia?: 'admin_override_pin'): Promise<ElevationVerifyResponse> {
      return fetchLocal('/api/elevation/verify', {
        method: 'POST',
        body: JSON.stringify(requireVia ? { pin, require_via: requireVia } : { pin }),
      });
    },
    clear(): Promise<{ ok: boolean }> {
      return fetchLocal('/api/elevation/clear', { method: 'POST' });
    },
  },

  // ── Security (PIN management — local → Next.js API routes) ──────────────
  security: {
    setPin(pin: string, currentPassword: string): Promise<{ ok: boolean }> {
      return fetchLocal('/api/account/security/pin', {
        method: 'POST',
        body: JSON.stringify({ pin, current_password: currentPassword }),
      });
    },
    deletePin(currentPassword: string): Promise<{ ok: boolean }> {
      return fetchLocal('/api/account/security/pin', {
        method: 'DELETE',
        body: JSON.stringify({ current_password: currentPassword }),
      });
    },
    setOverridePin(pin: string, currentPassword: string): Promise<{ ok: boolean }> {
      return fetchLocal('/api/account/security/override-pin', {
        method: 'POST',
        body: JSON.stringify({ pin, current_password: currentPassword }),
      });
    },
    deleteOverridePin(currentPassword: string): Promise<{ ok: boolean }> {
      return fetchLocal('/api/account/security/override-pin', {
        method: 'DELETE',
        body: JSON.stringify({ current_password: currentPassword }),
      });
    },
  },
};
